// ── Flavors: single source of truth ─────────────────────────────────────────
// Everything that defines a flavor lives in this folder:
//   registry.json — primitive build/store data (ids, hosts, backend, revcat)
//   settings.ts   — runtime FLAVOR_SETTING (asset imports, tags)
//   types.ts      — BusinessType / FlavorSetting unions
//   README.md     — the checklist for adding a flavor
// This barrel exposes typed access + the store-link helpers. quasar.config.js
// reads registry.json directly (Node can't import the TS here).

import registry from './registry.json'

export interface FlavorApple { bundleId: string; storeId: string } // iOS bundle id + numeric App Store id
export interface FlavorPlay { appId: string }                      // Android applicationId / Google Play id
export interface FlavorHosts { prod: string; dev: string }
export interface FlavorBackend { prod: string; dev: string }
export interface FlavorRevcat { apple: string; google: string; web: string; web_sandbox: string }
export interface FlavorDemo { email: string; password: string }
export interface FlavorEntry {
  apple?: FlavorApple
  play?: FlavorPlay
  hosts?: FlavorHosts
  backend: FlavorBackend
  revcat: FlavorRevcat
  demo?: FlavorDemo
}

// Strip the leading underscore doc keys (_README / _schema) — they're notes, not flavors.
const FLAVORS: Record<string, FlavorEntry> = Object.fromEntries(
  Object.entries(registry as Record<string, unknown>).filter(([k]) => !k.startsWith('_'))
) as Record<string, FlavorEntry>

/** Typed per-flavor registry (deployable/store flavors). */
export { FLAVORS }

// SINGLE SOURCE OF TRUTH (web) for the general/Plannera flavors: backend 'general' AND
// actually deployed (has a host). Derived from registry.json so it grows automatically
// when you add + deploy a general flavor — never hardcode this list elsewhere. Excludes
// not-yet-deployed general keys (newflv). The server mirrors it in
// cloud/flavors.js, guarded by src/flavors/registry.sync.test.ts.
export const GENERAL_FLAVORS: string[] = Object.entries(FLAVORS)
  .filter(([, f]) => (f as { backend?: { prod?: string }; hosts?: { prod?: string } }).backend?.prod === 'general'
    && (f as { hosts?: { prod?: string } }).hosts?.prod)
  .map(([k]) => k)

// ── Back-compat maps (were in src/data/flavor-app-ids.ts) ────────────────────
/** Android applicationId / Google Play id per flavor — non-empty only. */
export const FLAVOR_APP_IDS: Record<string, string> = Object.fromEntries(
  Object.entries(FLAVORS).filter(([, f]) => f.play?.appId).map(([k, f]) => [k, f.play!.appId])
)
/** Apple NUMERIC App Store ids per flavor — non-empty only. */
export const FLAVOR_APPLE_APP_IDS: Record<string, string> = Object.fromEntries(
  Object.entries(FLAVORS).filter(([, f]) => f.apple?.storeId).map(([k, f]) => [k, f.apple!.storeId])
)
/** iOS bundle ids (PRODUCT_BUNDLE_IDENTIFIER) per flavor — non-empty only. */
export const FLAVOR_IOS_BUNDLE_IDS: Record<string, string> = Object.fromEntries(
  Object.entries(FLAVORS).filter(([, f]) => f.apple?.bundleId).map(([k, f]) => [k, f.apple!.bundleId])
)

/** The Google Play applicationId for a flavor. */
export const appIdFor = (flavor: string): string | undefined => FLAVOR_APP_IDS[flavor]

/** Play Store URL for a flavor's app (works from the applicationId). */
export const playStoreUrl = (flavor: string): string | undefined => {
  const id = FLAVOR_APP_IDS[flavor]
  return id ? `https://play.google.com/store/apps/details?id=${id}` : undefined
}

/** App Store URL for a flavor's app (needs the numeric Apple id to be filled). */
export const appStoreUrl = (flavor: string): string | undefined => {
  const id = FLAVOR_APPLE_APP_IDS[flavor]
  return id ? `https://apps.apple.com/app/id${id}` : undefined
}

/** App Store "write a review" deep link — opens the review sheet straight away. */
export const appStoreReviewUrl = (flavor: string): string | undefined => {
  const id = FLAVOR_APPLE_APP_IDS[flavor]
  return id ? `https://apps.apple.com/app/id${id}?action=write-review` : undefined
}

/** Play Store review target for a flavor — its listing, where users tap "Rate this app".
 *  (Google Play has no dedicated web "write review" URL, unlike the App Store.) */
export const playStoreReviewUrl = (flavor: string): string | undefined => {
  const id = FLAVOR_APP_IDS[flavor]
  return id ? `https://play.google.com/store/apps/details?id=${id}` : undefined
}

/** Web host for a flavor (falls back to table's host). Mirrors quasar.config.js. */
export const hostFor = (flavor: string, isProd: boolean): string => {
  const hosts = FLAVORS[flavor]?.hosts || FLAVORS.table.hosts!
  return hosts[isProd ? 'prod' : 'dev']
}

// Re-exports so existing imports keep working.
export { FLAVOR_SETTING, supportsMultiDay, supportsHourlyPricing, unitPricingPeriod, resolveDurations, flavorImageDir, buildAddedUnitName } from './settings'
export type { BusinessType, FlavorSetting, OrderClassName, UnitClassName } from './types'
